'use client'

import { useQuery } from '@tanstack/react-query'
import { useLocale } from 'next-intl'

import { getAssetsV2 } from 'api/asset'
import { getAutoCompleteV2 } from 'api/search'
import { TEMP_USER_ID } from 'constant'
import { clientFetch } from 'lib/fetch/client'
import { useIpLocationStore } from 'store/server-context/ip-location'
import { useSessionStore } from 'store/server-context/session'
import type { IAssetV2 } from 'type/asset'
import type {
  ISearchCommunityV2,
  ISearchHistoryV2,
  ISearchUserV2
} from 'type/search'
import { normalizeAsset } from 'util/normalizeAsset'
import uniqueArray from 'util/uniqueArray'
import QueryItem from './query-item'
import SearchLink from './search-link'

interface IQueryCard {
  keyword: string
  debounceSearch: string
}

const QueryCard = ({ keyword, debounceSearch }: IQueryCard) => {
  const session = useSessionStore(state => state.session)
  const ipLocation = useIpLocationStore(state => state.ipLocation)
  const locale = useLocale()

  const { data, isLoading } = useQuery({
    queryKey: ['search', 'autoComplete', debounceSearch],
    queryFn: async () => {
      if (debounceSearch.length < 2) {
        return {
          user_list: [],
          community_list: []
        }
      }

      let lat = '0'
      let lng = '0'

      if (ipLocation) {
        const {
          ip_geo_point: { lat: ipLat, lng: ipLng }
        } = ipLocation
        lat = `${ipLat}`
        lng = `${ipLng}`
      }
      const query = [
        `keyword=${debounceSearch}`,
        `user_id=${session.guest ? TEMP_USER_ID : session.user_id}`,
        `lat=${lat}&lng=${lng}`,
        `country=${ipLocation?.country_code || ''}`,
        `lang=${locale}`
      ].join('&')

      try {
        const { data } = await getAutoCompleteV2(clientFetch, query)
        const assetIds = uniqueArray(
          (data?.user_list || []).map(user => user.headshot_id) ?? []
        )
        let assets: IAssetV2[] = []
        if (assetIds.length > 0) {
          const { data } = await getAssetsV2(clientFetch, assetIds.join(','))
          assets = data
        }
        const assetsMap = assets.reduce(
          (acc, asset) => {
            acc[asset.id] = asset
            return acc
          },
          {} as Record<string, IAssetV2>
        )
        return {
          user_list: data.user_list
            .map(u => {
              const headshotAsset = assetsMap[u?.headshot_id || '']
              const { src } = normalizeAsset(headshotAsset)

              return {
                ...u,
                head_shot_url: src
              }
            })
            .filter(u => u.uid),
          community_list: (data?.community_list ?? []).map(c => {
            const { src } = normalizeAsset(c.asset)
            return {
              ...c,
              community_cover: src
            }
          })
        }
      } catch (error) {
        console.log(error)
        return {
          user_list: [],
          community_list: []
        }
      }
    }
  })

  const userList = (data?.user_list || []).map(user => ({
    type: 'user',
    id: user.user_id,
    uid: user.uid,
    name: user.name,
    image: user.head_shot_url
  })) satisfies ISearchUserV2[]

  const communityList = (data?.community_list || []).map(community => ({
    type: 'community',
    id: community.id,
    title: community.title,
    count: community.community_joined_count,
    image: community.community_cover
  })) satisfies ISearchCommunityV2[]

  const list = [...userList, ...communityList]

  const keywordItem: ISearchHistoryV2 = {
    type: 'keyword',
    keyword: keyword,
    id: keyword
  }

  return (
    <>
      <SearchLink item={keywordItem}>
        <QueryItem item={keywordItem} />
      </SearchLink>

      {!isLoading &&
        list.map(item => (
          <SearchLink key={`search_${item.type}_${item.id}`} item={item}>
            <QueryItem item={item} />
          </SearchLink>
        ))}
    </>
  )
}

export default QueryCard
